<template>
  <div class="general-equilibrium-viz">
    
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$ΣF = 0 且 ΣM = 0$</div>
    </div>

    <div class="main-layout vertical">
      <!-- 仿真演示区 -->
      <div class="simulation-stage">
        <svg width="100%" height="200" viewBox="0 0 400 200">
          <!-- 地面线 -->
          <line x1="40" y1="180" x2="360" y2="180" stroke="#e2e8f0" stroke-width="2" />
          
          <!-- 梁/杠杆实体 -->
          <rect :x="80" :y="158" width="240" height="12" fill="#475569" rx="3" />

          <!-- A 支点 (固定在 100) -->
          <g transform="translate(100, 158)">
             <path d="M -10,22 L 10,22 L 0,0 Z" fill="#10b981" />
             <text x="-35" y="38" fill="#10b981" font-size="11" font-weight="700">支点 A</text>
             <text x="-35" y="52" fill="#64748b" font-size="10">Fa = {{ Fa.toFixed(1) }}N</text>
          </g>

          <!-- B 支点 (固定在 300) -->
          <g transform="translate(300, 158)">
             <path d="M -10,22 L 10,22 L 0,0 Z" fill="#10b981" />
             <text x="-15" y="38" fill="#10b981" font-size="11" font-weight="700">支点 B</text>
             <text x="-15" y="52" fill="#64748b" font-size="10">Fb = {{ Fb.toFixed(1) }}N</text>
          </g>

          <!-- 外力 F (动态移动) -->
          <g :transform="`translate(${forcePos}, 158)`">
             <!-- 力的大小矢量 -->
             <line x1="0" y1="-50" x2="0" y2="0" stroke="#ef4444" stroke-width="4" marker-end="url(#arrow-red)" />
             <text x="8" y="-40" fill="#ef4444" font-weight="900" font-size="13">F = {{ F }} N</text>
             <!-- 作用点标记 -->
             <circle r="4" fill="#ffffff" stroke="#ef4444" stroke-width="2" />
          </g>

          <!-- 重力 G (位于梁中心 200) -->
          <g transform="translate(200, 164)">
             <line x1="0" y1="0" x2="0" y2="25" stroke="#3b82f6" stroke-width="2" marker-end="url(#arrow-blue)" />
             <text x="6" y="20" fill="#3b82f6" font-size="11" font-weight="700">G = {{ beamWeight }} N</text>
             <circle r="3" fill="#3b82f6" />
          </g>

          <!-- 距离标注 L -->
          <g :transform="`translate(100, 130)`">
             <line x1="0" y1="0" :x2="forcePos - 100" y2="0" stroke="#94a3b8" stroke-dasharray="4,2" />
             <circle r="2" fill="#94a3b8" />
             <circle :cx="forcePos - 100" cy="0" r="2" fill="#94a3b8" />
             <text :x="(forcePos - 100)/2" y="-5" text-anchor="middle" fill="#64748b" font-size="11">距离 L = {{ forcePosOffset }}</text>
          </g>

          <defs>
            <marker id="arrow-red" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
               <path d="M0,0 L6,3 L0,6 Z" fill="#ef4444" />
            </marker>
            <marker id="arrow-blue" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
               <path d="M0,0 L6,3 L0,6 Z" fill="#3b82f6" />
            </marker>
          </defs>
        </svg>

        <!-- 方程求解面板 - 优化叠加层 -->
        <div class="equation-panel">
           <div class="panel-header">平衡状态解算</div>
           <div class="math-line">ΣF = Fa + Fb - F - G = 0</div>
           <div class="math-line">ΣM<sub>A</sub> = F·L + G·100 - Fb·200 = 0</div>
        </div>
      </div>

      <!-- 控制面板 -->
      <div class="compact-controls">
        <div class="control-card-grid">
          <div class="control-item">
             <div class="label-row">
                <span class="label">外力大小 (F)</span>
                <span class="value">{{ F }} N</span>
             </div>
             <el-slider v-model="F" :min="0" :max="100" size="small"></el-slider>
          </div>
          <div class="control-item">
             <div class="label-row">
                <span class="label">作用点位置 (L)</span>
                <span class="value">{{ forcePosOffset }} cm</span>
             </div>
             <el-slider v-model="forcePosOffset" :min="0" :max="200" size="small"></el-slider>
          </div>
        </div>

        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-info"></i>
           <p><strong>物理详解：</strong>一般物体的平衡（刚体平衡）必须满足<strong>合外力为零</strong>（保证不平动）且<strong>合外力矩为零</strong>（保证不转动）。改变外力位置会重新分配 A、B 两处的支持力，体现了力矩平衡的动态调整。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'GeneralEquilibriumViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      F: 40,
      forcePosOffset: 50,
      beamWeight: 20
    }
  },
  computed: {
    forcePos() { return 100 + this.forcePosOffset; },
    Fb() { return (this.F * this.forcePosOffset + this.beamWeight * 100) / 200; },
    Fa() { return this.F + this.beamWeight - this.Fb; }
  }
}
</script>

<style scoped lang="scss">
.general-equilibrium-viz { flex: 1; display: flex; flex-direction: column; gap: 12px; height: 100%; overflow: hidden; }

.viz-header-compact {
  display: flex; justify-content: space-between; align-items: center; padding: 0 4px;
  .brief { font-size: 14px; color: #1e293b; max-width: 70%; line-height: 1.4; }
  .formula-chip { background: #f0f7ff; color: #2563eb; border: 1px solid #dbeafe; padding: 6px 16px; border-radius: 20px; font-weight: 700; font-size: 13px; font-family: 'Consolas', monospace; }
}

.simulation-stage { 
  background: #ffffff; border-radius: 12px; position: relative; border: 1.5px solid #e2e8f0; padding: 10px; min-height: 240px; 
  svg { display: block; }
}

.equation-panel {
  position: absolute; top: 15px; left: 15px; background: rgba(255,255,255,0.9); border: 1px solid #e2e8f0; padding: 10px 14px; border-radius: 10px; box-shadow: 0 4px 12px rgba(0,0,0,0.05); pointer-events: none;
  .panel-header { font-size: 11px; font-weight: 800; color: #94a3b8; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 6px; border-bottom: 1px solid #f1f5f9; padding-bottom: 4px; }
  .math-line { font-family: 'Consolas', 'Courier New', monospace; font-size: 12px; color: #3b82f6; font-weight: 600; line-height: 1.6; }
}

.compact-controls { display: flex; flex-direction: column; gap: 10px; }
.control-card-grid {
  display: grid; grid-template-columns: 1fr 1fr; gap: 16px; background: #ffffff; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0;
  .control-item {
    .label-row { display: flex; justify-content: space-between; margin-bottom: 4px; .label { font-size: 13px; color: #64748b; font-weight: 600; } .value { font-size: 13px; color: #2563eb; font-weight: 700; } }
  }
}

</style>
